// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { GetBillTosApiParameter } from "@insite/client-framework/Services/CustomersService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getBillTosDataView } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadBillTos from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadBillTos";
import translate from "@insite/client-framework/Translate";
import { BillToModel, ShipToModel } from "@insite/client-framework/Types/ApiModels";
import DynamicDropdown, {
    DynamicDropdownPresentationProps,
    OptionObject,
} from "@steiner/MobiusOverrides/DynamicDropdown/DynamicDropdown";
import { color } from "@steiner/XDL/Utilities";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    noShipToAndCantCreate: boolean;
    setParameter: (parameter: GetBillTosApiParameter) => void;
    parameter: GetBillTosApiParameter;
    billTo?: BillToModel;
    onSelect: (billTo: BillToModel) => void;
    extendedStyles?: ChangeCustomerBillToSelectorStyles;
    isLoading?: boolean;
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => ({
    billTosDataView: getBillTosDataView(state, props.parameter),
    customerSettings: getSettingsCollection(state).customerSettings,
});

const mapDispatchToProps = {
    loadBillTos,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ChangeCustomerBillToSelectorStyles {
    billToSelectorDynamicDropdown?: DynamicDropdownPresentationProps;
}

export const changeCustomerBillToSelectorStyles: ChangeCustomerBillToSelectorStyles = {};

const ChangeCustomerBillToSelector: FC<Props> = ({
    noShipToAndCantCreate,
    parameter,
    setParameter,
    billTo,
    onSelect,
    extendedStyles,
    isLoading,
    billTosDataView,
}) => {
    const [styles] = useState(() => mergeToNew(changeCustomerBillToSelectorStyles, extendedStyles));
    const [options, setOptions] = useState<OptionObject[]>([]);
    const [billTosCount, setBillTosCount] = useState("");

    useEffect(() => {
        if (billTosDataView.value) {
            const options: OptionObject[] = billTosDataView.value.map(billTo => ({
                optionText: billTo.label,
                optionValue: billTo.id,
            }));
            setOptions(options);

            // XNG-Changes:
            if (billTosDataView.pagination) {
                const showCountInfo =
                    billTosDataView.pagination &&
                    billTosDataView.pagination?.totalItemCount > billTosDataView.pagination.pageSize;
                setBillTosCount(
                    showCountInfo
                        ? `Showing 1-${billTosDataView.pagination.pageSize} of ${billTosDataView.pagination.totalItemCount}`
                        : "",
                );
            }
        }
    }, [billTosDataView]);

    const searchTextChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
        setParameter({
            ...parameter,
            filter: event.currentTarget.value,
        });
    };

    const selectCustomerHandler = (billToId?: string) => {
        if (billTosDataView.value && billToId) {
            const billTo = billTosDataView.value.filter(billTo => billTo.id === billToId)[0];
            if (billTo) {
                onSelect(billTo as BillToModel);
            }
        }
    };

    // XNG-Changes:
    const dropdownClearHandler = () => {
        if (billTo?.id) {
            onSelect({} as BillToModel);
        }
        if (parameter.filter) {
            const { filter, ...rest } = parameter;
            setParameter(rest);
        }
    };

    // XNG-Changes: added  billTo total count to the dropdown + added clear icon
    return (
        <>
            <DynamicDropdown
                label={translate("Select Bill To")}
                {...styles.billToSelectorDynamicDropdown}
                onSelectionChange={selectCustomerHandler}
                onInputChange={searchTextChanged}
                selected={billTo?.id}
                options={options}
                placeholder={translate("Search or Select Bill To")}
                isLoading={isLoading}
                error={noShipToAndCantCreate && siteMessage("SignIn_NoShipToAndCantCreate")}
                data-test-selector="changeCustomerBillToSelector"
                filterOption={() => true}
                // XNG-Changes
                search={parameter.filter || ""}
                {...{
                    stickyHeaderOption: billTosCount,
                }}
                clearClickableProps={{
                    search: true,
                    hideCancelButton: !(parameter.filter || billTo?.id),
                    clickableProps: {
                        onClick: (e: any) => dropdownClearHandler(),
                    },
                }}
            />
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ChangeCustomerBillToSelector);
